import { UserType } from "@/types/AuthTypes";
import { imagesApi } from "@/api/images";
import { InvitationValidationResponse } from "@/types/InvitationTypes";
import React from "react";

/**
 * Parse style string to extract all CSS properties
 * Supports both formats:
 * - "width 300px height 200px" (simple format)
 * - "width: 300px; height: 200px; background: red;" (CSS format)
 * @param styleString - Style string from database
 * @returns Object with CSS properties, or null if parsing fails
 */
const parseStyleString = (
  styleString: string | undefined
): React.CSSProperties | null => {
  if (!styleString || typeof styleString !== "string") {
    return null;
  }

  const result: React.CSSProperties = {};

  // First, try to parse CSS-like format: "width: 300px; height: 200px; background: red;"
  const cssPropertyRegex = /([a-zA-Z-]+)\s*:\s*([^;]+);?/g;
  let match;
  const cssMatches: { [key: string]: string } = {};

  while ((match = cssPropertyRegex.exec(styleString)) !== null) {
    const property = match[1].trim();
    const value = match[2].trim();
    cssMatches[property] = value;
  }

  // If we found CSS format properties, use them
  if (Object.keys(cssMatches).length > 0) {
    for (const [property, value] of Object.entries(cssMatches)) {
      // Convert kebab-case to camelCase for React
      const camelProperty = property.replace(/-([a-z])/g, (g) =>
        g[1].toUpperCase()
      ) as keyof React.CSSProperties;
      // CSS values are strings, which are valid for CSSProperties
      (result as Record<string, string | number>)[camelProperty] = value;
    }
  } else {
    // Fallback to simple format: "width 300px height 200px"
    const widthMatch = styleString.match(/width\s+(\d+px)/i);
    const heightMatch = styleString.match(/height\s+(\d+px)/i);

    if (widthMatch) {
      result.width = widthMatch[1];
    }
    if (heightMatch) {
      result.height = heightMatch[1];
    }
  }

  return Object.keys(result).length > 0 ? result : null;
};

/**
 * Extract filename from logo/background value (can be string or object)
 * @param value - Can be string (filename) or object with filename property
 * @returns Filename string or null
 */
const extractFilename = (
  value: string | { filename: string; style?: string } | undefined
): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value.trim() === "" ? null : value;
  }

  if (typeof value === "object" && value.filename) {
    return typeof value.filename === "string" && value.filename.trim() !== ""
      ? value.filename
      : null;
  }

  return null;
};

/**
 * Extract style from logo/background value
 * @param value - Can be string (filename) or object with filename and style
 * @returns Style string or null
 */
const extractStyle = (
  value: string | { filename: string; style?: string } | undefined
): string | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value.style && typeof value.style === "string" ? value.style : null;
};

/**
 * Get logo data (filename and style) for a specific context
 * Priority: Group customizations (if exactly 1 group) > Client customizations
 * @param user - User object with sf_clients and sf_groups arrays
 * @param context - Context key (e.g., 'top_bar', 'phone', 'main_frame', 'left_menu_frame', 'training_screen')
 * @returns Object with filename and parsed style, or null if not available
 */
export const getClientLogoData = (
  user: UserType | null,
  context:
    | "top_bar"
    | "phone"
    | "main_frame"
    | "left_menu_frame"
    | "training_screen"
): { filename: string; style?: React.CSSProperties } | null => {
  if (!user) {
    return null;
  }

  // Priority 1: Group customizations (only if user has exactly 1 group)
  if (user.sf_groups && user.sf_groups.length === 1) {
    const group = user.sf_groups[0];
    if (
      group.customizations &&
      typeof group.customizations === "object" &&
      group.customizations.logo &&
      typeof group.customizations.logo === "object"
    ) {
      const logoValue = group.customizations.logo[context];
      const filename = extractFilename(logoValue);
      if (filename) {
        const styleString = extractStyle(logoValue);
        const parsedStyle = styleString ? parseStyleString(styleString) : null;
        return {
          filename,
          style: parsedStyle ?? undefined,
        };
      }
    }
  }

  // Priority 2: Client customizations (if 0 groups or multiple groups)
  if (!user.sf_clients || user.sf_clients.length === 0) {
    return null;
  }

  const client = user.sf_clients[0];
  if (!client.customizations || typeof client.customizations !== "object") {
    return null;
  }

  if (
    !client.customizations.logo ||
    typeof client.customizations.logo !== "object"
  ) {
    return null;
  }

  const logoValue = client.customizations.logo[context];
  const filename = extractFilename(logoValue);
  if (!filename) {
    return null;
  }

  const styleString = extractStyle(logoValue);
  const parsedStyle = styleString ? parseStyleString(styleString) : null;

  return {
    filename,
    style: parsedStyle ?? undefined,
  };
};

/**
 * Get client logo URL and style for top_bar context
 * @param user - User object with sf_clients array
 * @returns Promise resolving to object with url and style, or null
 */
export const getClientTopBarLogoUrl = async (
  user: UserType | null
): Promise<{ url: string; style?: React.CSSProperties } | null> => {
  const logoData = getClientLogoData(user, "top_bar");
  if (!logoData) {
    return null;
  }

  const { filename, style } = logoData;

  // If it's already a full URL, return it
  if (filename.startsWith("http://") || filename.startsWith("https://")) {
    return { url: filename, style };
  }

  // Otherwise, it's a filename - fetch it via API with client_image type
  try {
    const blob = await imagesApi.getClientImage(filename);
    const url = typeof blob === "string" ? blob : URL.createObjectURL(blob);
    return { url, style };
  } catch (error) {
    console.error("Failed to fetch client logo:", error);
    return null;
  }
};

/**
 * Get client logo URL and style for training_screen context
 * @param user - User object with sf_clients array
 * @returns Promise resolving to object with url and style, or null
 */
export const getClientTrainingScreenLogoUrl = async (
  user: UserType | null
): Promise<{ url: string; style?: React.CSSProperties } | null> => {
  const logoData = getClientLogoData(user, "training_screen");
  if (!logoData) {
    return null;
  }

  const { filename, style } = logoData;

  // If it's already a full URL, return it
  if (filename.startsWith("http://") || filename.startsWith("https://")) {
    return { url: filename, style };
  }

  // Otherwise, it's a filename - fetch it via API with client_image type
  try {
    const blob = await imagesApi.getClientImage(filename);
    const url = typeof blob === "string" ? blob : URL.createObjectURL(blob);
    return { url, style };
  } catch (error) {
    console.error("Failed to fetch client logo:", error);
    return null;
  }
};

/**
 * Get background data (filename and style) for a specific context
 * Priority: Group customizations (if exactly 1 group) > Client customizations
 * @param user - User object with sf_clients and sf_groups arrays
 * @param context - Context key (e.g., 'training_screen', 'phone', 'main_frame', 'left_menu_frame', 'top_bar')
 * @returns Object with filename and parsed style, or null if not available
 */
export const getClientBackgroundData = (
  user: UserType | null,
  context:
    | "training_screen"
    | "phone"
    | "main_frame"
    | "left_menu_frame"
    | "top_bar"
): { filename: string; style?: React.CSSProperties } | null => {
  if (!user) {
    return null;
  }

  // Priority 1: Group customizations (only if user has exactly 1 group)
  if (user.sf_groups && user.sf_groups.length === 1) {
    const group = user.sf_groups[0];
    if (
      group.customizations &&
      typeof group.customizations === "object" &&
      group.customizations.background &&
      typeof group.customizations.background === "object"
    ) {
      const backgroundValue = group.customizations.background[context];
      const filename = extractFilename(backgroundValue);
      if (filename) {
        const styleString = extractStyle(backgroundValue);
        const parsedStyle = styleString ? parseStyleString(styleString) : null;
        return {
          filename,
          style: parsedStyle ?? undefined,
        };
      }
    }
  }

  // Priority 2: Client customizations (if 0 groups or multiple groups)
  if (!user.sf_clients || user.sf_clients.length === 0) {
    return null;
  }

  const client = user.sf_clients[0];
  if (!client.customizations || typeof client.customizations !== "object") {
    return null;
  }

  if (
    !client.customizations.background ||
    typeof client.customizations.background !== "object"
  ) {
    return null;
  }

  const backgroundValue = client.customizations.background[context];
  const filename = extractFilename(backgroundValue);
  if (!filename) {
    return null;
  }

  const styleString = extractStyle(backgroundValue);
  const parsedStyle = styleString ? parseStyleString(styleString) : null;

  return {
    filename,
    style: parsedStyle ?? undefined,
  };
};

/**
 * Get client background URL and style for training_screen context
 * @param user - User object with sf_clients array
 * @returns Promise resolving to object with url and style, or null
 */
export const getClientTrainingScreenBackgroundUrl = async (
  user: UserType | null
): Promise<{ url: string; style?: React.CSSProperties } | null> => {
  const backgroundData = getClientBackgroundData(user, "training_screen");
  if (!backgroundData) {
    return null;
  }

  const { filename, style } = backgroundData;
  // If it's already a full URL, return it
  if (filename.startsWith("http://") || filename.startsWith("https://")) {
    return { url: filename, style };
  }

  // Otherwise, it's a filename - fetch it via API with client_image type
  try {
    const blob = await imagesApi.getClientImage(filename);
    console.log(" blob 3: ", blob);
    const url = typeof blob === "string" ? blob : URL.createObjectURL(blob);
    return { url, style };
  } catch (error) {
    console.error("Failed to fetch client background:", error);
    return null;
  }
};

/**
 * Get logo data from invitation customizations for a specific context
 * Priority: Group customizations > Client customizations
 * @param invitationData - Invitation validation response with customizations
 * @param context - Context key (e.g., 'top_bar', 'phone', 'main_frame', 'left_menu_frame', 'training_screen')
 * @returns Object with filename and parsed style, or null if not available
 */
export const getInvitationLogoData = (
  invitationData: InvitationValidationResponse | null,
  context:
    | "top_bar"
    | "phone"
    | "main_frame"
    | "left_menu_frame"
    | "training_screen"
): { filename: string; style?: React.CSSProperties } | null => {
  if (!invitationData) {
    return null;
  }

  // Priority 1: Group customizations (if available)
  if (
    invitationData.group_customizations &&
    typeof invitationData.group_customizations === "object" &&
    invitationData.group_customizations.logo &&
    typeof invitationData.group_customizations.logo === "object"
  ) {
    const logoValue = invitationData.group_customizations.logo[context];
    const filename = extractFilename(logoValue);
    if (filename) {
      const styleString = extractStyle(logoValue);
      const parsedStyle = styleString ? parseStyleString(styleString) : null;
      return {
        filename,
        style: parsedStyle ?? undefined,
      };
    }
  }

  // Priority 2: Client customizations
  if (
    invitationData.client_customizations &&
    typeof invitationData.client_customizations === "object" &&
    invitationData.client_customizations.logo &&
    typeof invitationData.client_customizations.logo === "object"
  ) {
    const logoValue = invitationData.client_customizations.logo[context];
    const filename = extractFilename(logoValue);
    if (filename) {
      const styleString = extractStyle(logoValue);
      const parsedStyle = styleString ? parseStyleString(styleString) : null;
      return {
        filename,
        style: parsedStyle ?? undefined,
      };
    }
  }

  return null;
};

/**
 * Get invitation logo URL and style for top_bar context
 * @param invitationData - Invitation validation response with customizations
 * @returns Promise resolving to object with url and style, or null
 */
export const getInvitationTopBarLogoUrl = async (
  invitationData: InvitationValidationResponse | null
): Promise<{ url: string; style?: React.CSSProperties } | null> => {
  const logoData = getInvitationLogoData(invitationData, "top_bar");
  if (!logoData) {
    return null;
  }

  const { filename, style } = logoData;

  // If it's already a full URL, return it
  if (filename.startsWith("http://") || filename.startsWith("https://")) {
    return { url: filename, style };
  }

  // Otherwise, it's a filename - fetch it via API with client_image type
  try {
    const blob = await imagesApi.getClientImage(filename);
    const url = typeof blob === "string" ? blob : URL.createObjectURL(blob);
    return { url, style };
  } catch (error) {
    console.error("Failed to fetch invitation logo:", error);
    return null;
  }
};
